export interface DependencyTableRow {
    dependencyName: string;
    version: string;
    vendorGuidanceStatus: string;
    recommendedVersion?: string;
    riskStatus: string;
}

/** AT_RISK is what the UI calls the LOW_RISK status from the backend */
export const AT_RISK_LABEL = "AT_RISK";

export const NOT_AVAILABLE = "Not Available";

/** "NA" is how dry-run and leadership backend refer to missing version */
export const hasRecommendedVersion = (version?: string): version is string =>
    !!version && version !== "NA";

export type GuidancePolarity =
    | "STRONG_NEGATIVE"
    | "WEAK_NEGATIVE"
    | "NEUTRAL"
    | "POSITIVE";

/** Mirrors FALLBACK_POLARITY in the DependencySentinelCore's VendorGuidanceUtils */
const GUIDANCE_POLARITY_BY_LABEL: Record<string, GuidancePolarity> = {
    END_OF_LIFE: "STRONG_NEGATIVE",
    UPCOMING_END_OF_LIFE: "STRONG_NEGATIVE",
    RECALLED: "STRONG_NEGATIVE",
    DEPRECATED: "STRONG_NEGATIVE",
    UNSUPPORTED: "STRONG_NEGATIVE",
    AVAILABILITY_CONCERN: "STRONG_NEGATIVE",
    KNOWN_BUGS: "STRONG_NEGATIVE",
    PROHIBITED: "STRONG_NEGATIVE",
    INFOSEC_RECALLED: "STRONG_NEGATIVE",
    INFO_SEC_RECALLED: "STRONG_NEGATIVE",
    AWS_SEC_RECALLED: "STRONG_NEGATIVE",
    BLOCKED: "STRONG_NEGATIVE",
    NEW_USAGE_PROHIBITED: "STRONG_NEGATIVE",
    NEWER_VERSION_AVAILABLE: "WEAK_NEGATIVE",
    EXPERIMENTAL: "WEAK_NEGATIVE",
    UNSTABLE: "WEAK_NEGATIVE",
    AT_RISK: "WEAK_NEGATIVE",
    ALLOWED: "NEUTRAL",
    NONE: "NEUTRAL",
    RECOMMENDED: "POSITIVE",
    APPROVED: "POSITIVE",
    LONG_TERM_SUPPORT: "POSITIVE",
};

export const guidancePolarity = (label?: string): GuidancePolarity | undefined =>
    GUIDANCE_POLARITY_BY_LABEL[(label ?? "").toUpperCase()];

export const RISK_ORDER = ["HIGH_RISK", "LOW_RISK", "UPGRADE_AVAILABLE", "NO_ACTION_REQUIRED"];

const CR_ELIGIBLE_RISK_STATUSES = ["LOW_RISK", "UPGRADE_AVAILABLE"];

export const raisesCodeReviews = (riskStatus?: string): boolean =>
    CR_ELIGIBLE_RISK_STATUSES.includes(riskStatus ?? "");

export const riskRank = (status?: string): number => {
    const index = RISK_ORDER.indexOf(status ?? "");
    return index === -1 ? RISK_ORDER.length : index;
};

export const compareByRisk = (
    a: { dependencyName: string; riskStatus: string },
    b: { dependencyName: string; riskStatus: string },
): number =>
    riskRank(a.riskStatus) - riskRank(b.riskStatus) ||
    a.dependencyName.localeCompare(b.dependencyName);

export const filterableText = (row: DependencyTableRow): string =>
    [
        row.dependencyName,
        row.version,
        row.vendorGuidanceStatus,
        hasRecommendedVersion(row.recommendedVersion)
            ? row.recommendedVersion
            : NOT_AVAILABLE,
        row.riskStatus,
        row.riskStatus === "LOW_RISK" ? AT_RISK_LABEL : "",
    ]
        .join(" ")
        .toLowerCase();
